/**
 * One-shot utility for fire-and-forget worker communication.
 * Creates a worker, posts a single message, returns a promise with the response,
 * and terminates the worker.
 *
 * Usage:
 *   const result = await callWorker<InputType, OutputType>(
 *     () => new MyWorker(),
 *     { someData: 123 },
 *   );
 */
export function callWorker<TInput, TOutput>(
  createWorker: () => Worker,
  data: TInput,
  transferables?: Transferable[],
): Promise<TOutput> {
  return new Promise<TOutput>((resolve, reject) => {
    const worker = createWorker();

    worker.onmessage = (event: MessageEvent<TOutput>) => {
      worker.terminate();
      resolve(event.data);
    };

    worker.onerror = (error: ErrorEvent) => {
      worker.terminate();
      // An ErrorEvent without a message means the worker script itself failed to load
      // (network error, or a stale chunk after a deploy). Errors thrown inside the worker carry a message.
      reject(new Error(error.message ? `Worker error: ${error.message} (${error.filename}:${error.lineno})` : 'Worker script failed to load'));
    };

    if (transferables) {
      worker.postMessage(data, transferables);
    } else {
      worker.postMessage(data);
    }
  });
}
